import { useLazyQuery } from '@apollo/client'

import { useEffect, useRef, useState } from 'react'

import {
  Box,
  Flex,
  FloraInput,
  FloraRadio,
  FloraSelect,
  FloraSelectItem,
  FloraTypography,
  Tooltip
} from '@grupoboticario/flora-react'
import { InformationCircleIcon } from '@grupoboticario/flora-react-icons'

import HeadingAccelerator from '@/components/HeadingAccelerator/HeadingAccelerator'
import RichTextEditor from '@/components/RichTextEditor/RichTextEditor'
import { VISUAL_DESCRIPTION_MAX, VISUAL_TITLE_MAX } from '@/constants/accelerationForm'
import { ACCELERATION_STEPPER_VARIANT } from '@/constants/accelerationStepper'
import { CampaignBrandCode } from '@/constants/campaignBrands'
import { GET_EVENT_SIGNED_URL } from '@/graphql/queries/getEventSignedUrl'
import { getHtmlTextLength, sanitizeHtmlForMobileApp } from '@/utils/htmlHelpers/htmlHelpers'

import {
  AccelerationContentFields,
  CampaignBrandFlags,
  ContentByBrandForm,
  AccelerationStepperVariant
} from '../AccelerationForm.types'
import CampaignBrandsSection from './CampaignBrandsSection'
import CharacterCounter from './counter/CharacterCounter'
import AccelerationImageUpload from './image-upload/AccelerationImageUpload'
import AcceleratorVisualPreview from './preview/AcceleratorVisualPreview'

const BUTTON_TEXT_OPTIONS = [
  { label: 'Comprar agora', value: 'Comprar agora' },
  { label: 'Acessar página', value: 'Acessar página' }
]

interface StepDesignProps {
  values: AccelerationContentFields
  brand: CampaignBrandFlags
  contentByBrand: ContentByBrandForm
  readOnly?: boolean
  variant: AccelerationStepperVariant
  previewImageDataUrl: string | null
  onPreviewImageDataUrlChange: (url: string | null) => void
  onChange: (field: keyof Omit<AccelerationContentFields, 'details' | 'showModal'>, value: string) => void
  onShowModalChange: (value: boolean) => void
  onBrandChange: (brandCode: CampaignBrandCode, checked: boolean) => void
  onBrandButtonUrlChange: (brandCode: CampaignBrandCode, buttonUrl: string) => void
}

const StepDesign = ({
  values,
  brand,
  contentByBrand,
  readOnly = false,
  variant,
  previewImageDataUrl,
  onPreviewImageDataUrlChange,
  onChange,
  onShowModalChange,
  onBrandChange,
  onBrandButtonUrlChange
}: StepDesignProps) => {
  const descriptionRef = useRef<HTMLDivElement>(null)
  const [descriptionCharCount, setDescriptionCharCount] = useState(() => getHtmlTextLength(values.description ?? ''))
  const [getSignedUrl] = useLazyQuery(GET_EVENT_SIGNED_URL, { fetchPolicy: 'network-only' })

  useEffect(() => {
    if (descriptionRef.current && values.description) {
      descriptionRef.current.innerHTML = sanitizeHtmlForMobileApp(values.description)
    }
  }, [])

  const handleDescriptionChange = (textContent: string) => {
    setDescriptionCharCount(textContent.length)

    const html = sanitizeHtmlForMobileApp(descriptionRef.current?.innerHTML ?? '')
    onChange('description', html)
  }

  const handleInputChange =
    (field: keyof Omit<AccelerationContentFields, 'details' | 'showModal'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(field, e.target.value)

  return (
    <Flex direction="column" gap="$4">
      <Flex gap="$3">
        <Box css={{ flex: 1 }}>
          <FloraInput
            data-testid="visualTitleInput"
            id="visualTitle"
            label="Título do acelerador"
            maxLength={VISUAL_TITLE_MAX}
            onChange={handleInputChange('title')}
            readOnly={readOnly}
            disabled={readOnly}
            required
            size="medium"
            value={values.title}
          />
          {!readOnly && <CharacterCounter current={values.title.length} max={VISUAL_TITLE_MAX} />}
        </Box>
        <Box css={{ flex: 1 }}>
          <FloraSelect
            data-testid="buttonTextSelect"
            id="buttonText"
            label="Texto do botão"
            onSelected={(selected) => onChange('buttonText', selected?.value ?? '')}
            placeholder="Selecionar"
            disabled={readOnly}
            required
            size="medium"
            value={values.buttonText}
          >
            {BUTTON_TEXT_OPTIONS.map(({ label, value }) => (
              <FloraSelectItem key={value} label={label} value={value} />
            ))}
          </FloraSelect>
        </Box>
      </Flex>

      <Box>
        <RichTextEditor
          editorRef={descriptionRef}
          formattingCommands={['bold']}
          maxTextLength={VISUAL_DESCRIPTION_MAX}
          minHeight="50"
          maxHeight="100px"
          isReadOnly={readOnly}
          onBlur={() => {
            const html = sanitizeHtmlForMobileApp(descriptionRef.current?.innerHTML ?? '')
            onChange('description', html)
          }}
          onInput={(text) => {
            handleDescriptionChange(text)
          }}
          placeholder="Breve descrição"
        />
        {!readOnly && <CharacterCounter current={descriptionCharCount} max={VISUAL_DESCRIPTION_MAX} />}
      </Box>
      <CampaignBrandsSection
        brand={brand}
        contentByBrand={contentByBrand}
        buttonUrl={values.buttonUrl}
        allowLegacyButtonUrl={variant !== ACCELERATION_STEPPER_VARIANT.CREATE}
        readOnly={readOnly}
        onBrandChange={onBrandChange}
        onBrandButtonUrlChange={onBrandButtonUrlChange}
        onButtonUrlChange={(value) => onChange('buttonUrl', value)}
      />
      <Box css={{ backgroundColor: '$backgroundSecondary', padding: '$2' }}>
        <AccelerationImageUpload
          imageUrl={values.imageUrl || undefined}
          disabled={readOnly}
          getSignedUrl={getSignedUrl}
          onBase64Change={(base64) => onPreviewImageDataUrlChange(base64)}
          onImageChange={(_previewUrl, imageKey) => onChange('imageKey', imageKey)}
        />
      </Box>

      <Box css={{ marginTop: '$10' }}>
        <fieldset disabled={readOnly || variant === ACCELERATION_STEPPER_VARIANT.EDIT}>
          <legend>
            <Flex align="center" gap="$2">
              <FloraTypography fontSize="bodySmallStandard">Ativar modal</FloraTypography>
              <Tooltip
                align="center"
                autotagging-flora="off"
                direction="top"
                hasPortal
                showArrow
                text="Lembre-se: O modal “Novo acelerador disponível” será exibido apenas uma vez para o usuário."
                variant="standard"
              >
                <InformationCircleIcon color="$statusInfoActionableDefault" size={20} />
              </Tooltip>
            </Flex>
          </legend>
          <Flex gap="$4" css={{ marginTop: '$4' }}>
            <FloraRadio
              checked={values.showModal}
              onChange={() => onShowModalChange(true)}
              id="modal-yes"
              name="modal"
              value="yes"
              label="Sim"
            />
            <FloraRadio
              checked={!values.showModal}
              onChange={() => onShowModalChange(false)}
              id="modal-no"
              name="modal"
              value="no"
              label="Não"
            />
          </Flex>
        </fieldset>
      </Box>

      <Box>
        <HeadingAccelerator title="Preview do acelerador" />
        <AcceleratorVisualPreview values={values} base64Image={previewImageDataUrl} />
      </Box>
    </Flex>
  )
}

export default StepDesign
